import { useMemo } from 'react';
import type { FilterLineType } from '../../../../components/new-design/vyTableFilter';
import { useRecurringRevenueReport } from '../../../../hooks/requests/use-recurring-revenue-report';
import { useRecurringRevenueAggregations } from '../../recurring-revenue-report-page/hooks/use-recurring-revenue-aggregations';
import { filterMonthKeysByDateRange } from '../../recurring-revenue-report-page/utils/chart-movements';

export type RetentionChartPoint = {
  month: string;
  nrr: number;
  grr: number;
};

/**
 * Returns NRR and GRR points (oldest → newest) within the given date range.
 * The heavy aggregation runs in the recurring-revenue Web Worker; the date
 * window is applied here on the main thread so pans don't re-run the worker.
 * Falls back to the trailing 12 months when no dates are provided.
 */
export function useRetentionChartData(
  fromDate: Date | null,
  toDate: Date | null,
  filters: FilterLineType[] = [],
): RetentionChartPoint[] {
  const { data } = useRecurringRevenueReport();
  const { report } = data;

  const { bundle } = useRecurringRevenueAggregations({ report, filters });

  return useMemo(() => {
    const all = bundle?.retentionPoints ?? [];

    if (!fromDate && !toDate) {
      return all.slice(-12).map(toChartPoint);
    }

    const monthKeys = all.map((point) => point.monthKey);
    const visible = new Set(filterMonthKeysByDateRange(monthKeys, fromDate, toDate));
    return all.filter((point) => visible.has(point.monthKey)).map(toChartPoint);
  }, [bundle, fromDate, toDate]);
}

function toChartPoint(point: { month: string; nrr: number; grr: number }): RetentionChartPoint {
  return { month: point.month, nrr: point.nrr, grr: point.grr };
}
